import { useContext } from 'react';
import { format, isToday, isYesterday, isThisYear } from 'date-fns';

import { Card, Text, TextButton } from '@zillow/constellation-10';
import { LoadingParagraph, LoadingTag, LoadingText } from '@zg-rentals/loading-components';

import SimpleCard from '../../../SimpleCard';

import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';

import { RoutingSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';
import { LoadingMapContext } from '../../../../providers/LoadingMapContext/context';
import { RMWContext } from '../../../../providers/RMWContext';
import { getTagTypeByKey, STATUS_KEY } from '../../../../utils/statusTagUtils';
import { getDisplayTextForApplicationStatus } from '../../../../utils/applicationStatusUtils';
import { APPLICATION_STATUS_TYPE } from '../../../../constants/ApplicationStatusConstants';

export type Applicant = {
  name: string;
  status: string;
};

export type Application = {
  statusType: string;
  applicants: Array<Applicant>;
  applicationIsViewed: boolean;
  statusUpdatedTimestamp: number;
  conversationId: string;
  applicationId: string;
};

type Props = {
  application: Application;
  displayType?: VERTICAL_DISPLAY_TYPE;
};

const getApplicantsDisplayText = (applicants: Array<Applicant> = []) => {
  if (!applicants.length) {
    return '';
  }

  let displayText = '';
  let additionalApplicantsCount = 0;

  applicants.forEach((applicant, idx) => {
    // If first item
    if (!idx) {
      displayText = applicant.name;
    } else {
      additionalApplicantsCount += 1;
    }
  });

  if (additionalApplicantsCount) {
    displayText += ` + ${additionalApplicantsCount}`;
  }

  return displayText;
};

const getNumTotalCompletedApplicants = (applicants: Array<{ status?: string }> = []) => {
  if (!applicants.length) {
    return 0;
  }

  let numTotalCompletedApplicants = 0;

  applicants.forEach((applicant) => {
    if (applicant.status === 'completed') {
      numTotalCompletedApplicants += 1;
    }
  });

  return numTotalCompletedApplicants;
};

const getTimestamp = (statusUpdatedTimestamp: number) => {
  const isTimestampToday = isToday(statusUpdatedTimestamp);
  const isTimestampYesterday = isYesterday(statusUpdatedTimestamp);

  let timestamp = format(statusUpdatedTimestamp, 'iiii, MMM d');

  if (!isThisYear(statusUpdatedTimestamp)) {
    timestamp = format(statusUpdatedTimestamp, 'MMM d, yyyy');
  } else if (isTimestampToday || isTimestampYesterday) {
    const formattedTime = format(statusUpdatedTimestamp, 'h:mm a');
    const dayText = isTimestampToday ? 'Today' : 'Yesterday';

    timestamp = `${dayText} at ${formattedTime}`;
  }

  return timestamp;
};

const ApplicationItem = ({
  application: { statusType, applicants, applicationIsViewed, statusUpdatedTimestamp, conversationId, applicationId },
  displayType = VERTICAL_DISPLAY_TYPE.FULL,
}: Props) => {
  const { paths } = useContext(RMWContext);
  const {
    loadingByComponent: { ConnectedApplications: isLoading },
  } = useContext(LoadingMapContext);

  // TODO: refactor to use listingAlias
  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  const applicantsDisplayName = getApplicantsDisplayText(applicants);
  const connectedStatusType = isLoading ? 'gray' : getTagTypeByKey(STATUS_KEY.INTERESTED_RENTERS, statusType);
  const isApplicationReceived = statusType === APPLICATION_STATUS_TYPE.COMPLETED;
  const statusDisplayText = getDisplayTextForApplicationStatus(statusType);
  const showNewBadge = isApplicationReceived && !applicationIsViewed;
  const numTotalCompletedApplicants = getNumTotalCompletedApplicants(applicants);

  const SubText = () => {
    if (statusType === APPLICATION_STATUS_TYPE.PENDING) {
      return (
        <div data-testid="SubTextContainer">
          <Text textStyle="body-sm" css={{ paddingTop: '200' }}>
            {`${numTotalCompletedApplicants} of ${applicants.length} have applied`}
          </Text>
        </div>
      );
    }

    const timestamp = isLoading ? null : getTimestamp(statusUpdatedTimestamp);
    return (
      <div data-testid="SubTextContainer">
        {(showNewBadge || isLoading) && (
          <>
            <LoadingText
              textStyle="body-sm"
              css={{ color: 'text.feedback.warning.hero', marginRight: isLoading ? '300' : 'none' }}
              isLoading={isLoading}
              loadingWidth="25%"
            >
              New
            </LoadingText>
            {!isLoading && ' · '}
          </>
        )}
        <LoadingText textStyle="body-sm" isLoading={isLoading} loadingWidth="50%">
          {timestamp}
        </LoadingText>
      </div>
    );
  };

  const handleCardClick = () => {
    let linkUrl = `${paths.base}/${paths.propertyDetailsApplications}/${applicationId}?propertyId=${listingId}`;

    if (statusType === APPLICATION_STATUS_TYPE.INVITED || statusType === APPLICATION_STATUS_TYPE.WITHDRAWN) {
      linkUrl = `${paths.base}/inbox/${listingId}/${conversationId}`;
    }

    window.router.updateLocation(linkUrl);
  };

  const cardContent = (
    <>
      <LoadingTag
        size="sm"
        tone={connectedStatusType}
        isLoading={isLoading}
        loadingWidth="20%"
        css={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {statusDisplayText}
      </LoadingTag>
      <LoadingParagraph
        textStyle="body-sm-bold"
        isLoading={isLoading}
        loadingWidth="100%"
        css={{
          textTransform: 'capitalize',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          paddingTop: '200',
          fontSize: { lg: 'body' },
        }}
        className="fs-mask"
      >
        {applicantsDisplayName}
      </LoadingParagraph>
      <SubText />

      {displayType === VERTICAL_DISPLAY_TYPE.FULL &&
        (isLoading ? (
          <LoadingText loadingWidth="20%" textStyle="body-sm-bold" isLoading={true}>
            View
          </LoadingText>
        ) : (
          <TextButton textStyle="body-sm-bold">
            {statusType === APPLICATION_STATUS_TYPE.INVITED || statusType === APPLICATION_STATUS_TYPE.WITHDRAWN
              ? 'Message'
              : 'View'}
          </TextButton>
        ))}
    </>
  );

  const renderCard = () => {
    if (displayType === VERTICAL_DISPLAY_TYPE.SIMPLE) {
      return (
        <SimpleCard withChevron={true} cursor="pointer" onClick={handleCardClick} data-testid="application-item">
          {cardContent}
        </SimpleCard>
      );
    }

    return (
      <Card.Root
        tone="neutral"
        elevated={false}
        data-testid="application-item"
        css={{ height: '150px', cursor: 'pointer', padding: 'none' }}
      >
        <Card.Body onClick={handleCardClick} css={{ padding: 'Card.Root.padding' }}>
          {cardContent}
        </Card.Body>
      </Card.Root>
    );
  };

  return renderCard();
};

export default ApplicationItem;
